import { useEffect, useRef, useState } from "react";
import { GripVertical, Loader2, Search, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

const MAX = 8;

/** Pick the articles shown under "Related articles" at the foot of a reader's
 *  page. Picks sit at the top in the order they'll appear and can be dragged;
 *  the search list underneath is everything else an editor can see. */
export function KbRelatedPicker({ slug, ids = [], title, bodyHtml, categoryId, onChange }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [picked, setPicked] = useState([]);        // full records for the ids
  const [suggested, setSuggested] = useState([]);
  const [busy, setBusy] = useState("");
  const dragFrom = useRef(null);

  // Titles for whatever is already related, so the list isn't a row of ids.
  useEffect(() => {
    if (!ids.length) { setPicked([]); return; }
    let alive = true;
    api.get("/kb/manage/articles", { params: { limit: 200, ids: ids.join(",") } })
      .then(({ data }) => {
        if (!alive) return;
        const byId = Object.fromEntries((data.articles || []).map((a) => [a.article_id, a]));
        setPicked(ids.map((id) => byId[id] || { article_id: id, name: id }));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [ids.join(",")]);           // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setTimeout(async () => {
      setBusy("list");
      try {
        const { data } = await api.get("/kb/manage/articles", {
          params: { q, limit: 25, sort: q.trim() ? "name" : "recent", content_type: "article" },
        });
        setRows((data.articles || []).filter((a) => a.slug !== slug));
      } catch { setRows([]); } finally { setBusy(""); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, slug]);

  const set = (next) => onChange(next.slice(0, MAX));
  const toggle = (id) => (ids.includes(id) ? set(ids.filter((x) => x !== id))
    : ids.length >= MAX ? toast.error(`Up to ${MAX} related articles`) : set([...ids, id]));

  const suggest = async () => {
    setBusy("ai");
    try {
      const { data } = await api.post("/kb/ai/suggest-related", {
        slug, title, body_html: bodyHtml, category_id: categoryId, exclude: ids,
      });
      setSuggested(data.suggestions || []);
      if (!data.suggestions?.length) toast.info(data.note || "Nothing close enough to suggest");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't get suggestions");
    } finally { setBusy(""); }
  };

  const drop = (to) => {
    const from = dragFrom.current;
    dragFrom.current = null;
    if (from === null || from === to) return;
    const next = [...ids];
    next.splice(to, 0, next.splice(from, 1)[0]);
    set(next);
  };

  return (
    <div className="space-y-2" data-testid="kb-related-picker">
      <div className="flex items-center justify-between">
        <Label className="text-xs">
          Related articles{" "}
          <span className="font-normal text-slate-400 dark:text-slate-500">
            ({ids.length}/{MAX}, shown in this order)
          </span>
        </Label>
        <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px]" onClick={suggest}
          disabled={busy === "ai" || !title?.trim()} data-testid="kb-related-suggest">
          {busy === "ai" ? <Loader2 className="mr-1 h-3 w-3 animate-spin" />
            : <Sparkles className="mr-1 h-3 w-3" />}
          Suggest
        </Button>
      </div>

      {!!picked.length && (
        <ul className="space-y-1" data-testid="kb-related-picked">
          {picked.map((a, i) => (
            <li key={a.article_id} draggable
              onDragStart={() => { dragFrom.current = i; }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => drop(i)}
              data-testid={`kb-related-picked-${a.article_id}`}
              className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1.5 dark:border-slate-700 dark:bg-slate-900">
              <GripVertical className="h-3.5 w-3.5 shrink-0 cursor-grab text-slate-300" />
              <span className="min-w-0 flex-1 truncate text-xs text-slate-700 dark:text-slate-200">{a.name}</span>
              {a.is_draft && <span className="shrink-0 rounded bg-amber-100 px-1 text-[10px] font-semibold uppercase text-amber-700">Draft</span>}
              <button type="button" onClick={() => set(ids.filter((x) => x !== a.article_id))}
                data-testid={`kb-related-remove-${a.article_id}`}
                className="shrink-0 rounded p-0.5 text-slate-400 transition-colors hover:text-rose-600">
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {!!suggested.length && (
        <div className="rounded-md border border-primary/30 bg-primary/5 p-2" data-testid="kb-related-suggestions">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-primary">Suggested</div>
          {suggested.map((s) => (
            <label key={s.article_id} data-testid={`kb-related-suggestion-${s.article_id}`}
              className="flex cursor-pointer items-start gap-2 rounded px-1 py-1 hover:bg-white/60 dark:hover:bg-slate-800/60">
              <input type="checkbox" className="mt-0.5" checked={ids.includes(s.article_id)}
                onChange={() => toggle(s.article_id)} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-xs text-slate-700 dark:text-slate-200">{s.name}</span>
                {!!s.why && <span className="block truncate text-[10px] text-slate-500 dark:text-slate-400">{s.why}</span>}
              </span>
            </label>
          ))}
        </div>
      )}

      <div className="relative">
        <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles…"
          data-testid="kb-related-search" className="h-8 pl-7 text-xs" />
      </div>
      <div className="max-h-56 space-y-0.5 overflow-y-auto rounded-md border border-slate-200 p-1 dark:border-slate-800"
        data-testid="kb-related-results">
        {busy === "list" && !rows.length && (
          <p className="px-1 py-2 text-[11px] text-slate-400">Looking…</p>
        )}
        {!busy && !rows.length && (
          <p className="px-1 py-2 text-[11px] text-slate-400">No articles matched.</p>
        )}
        {rows.map((a) => (
          <label key={a.article_id} data-testid={`kb-related-option-${a.article_id}`}
            className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 hover:bg-slate-50 dark:hover:bg-slate-800/60">
            <input type="checkbox" checked={ids.includes(a.article_id)}
              onChange={() => toggle(a.article_id)} />
            <span className="min-w-0 flex-1 truncate text-xs text-slate-600 dark:text-slate-300">{a.name}</span>
            {a.is_draft && <span className="shrink-0 rounded bg-amber-100 px-1 text-[10px] font-semibold uppercase text-amber-700">Draft</span>}
          </label>
        ))}
      </div>
    </div>
  );
}
